import audioSVG from '@plone/volto/icons/audio.svg';
import checkboxSVG from '@plone/volto/icons/checkbox-checked.svg';
import headingSVG from '@plone/volto/icons/heading.svg';
import imagesSVG from '@plone/volto/icons/images.svg';
import megaphoneSVG from '@plone/volto/icons/megaphone.svg';
import newsSVG from '@plone/volto/icons/news.svg';
import videoSVG from '@plone/volto/icons/video.svg';

import MpfAceiteTermosBlockEdit from 'volto-ministerio-publico-federal/components/Blocks/AceiteTermos/Edit';
import MpfAceiteTermosBlockView from 'volto-ministerio-publico-federal/components/Blocks/AceiteTermos/View';
import MpfCampanhasBlockEdit from 'volto-ministerio-publico-federal/components/Blocks/Campanhas/Edit';
import MpfCampanhasBlockView from 'volto-ministerio-publico-federal/components/Blocks/Campanhas/View';
import MpfGaleriaImagensBlockEdit from 'volto-ministerio-publico-federal/components/Blocks/GaleriaImagens/Edit';
import MpfGaleriaImagensBlockView from 'volto-ministerio-publico-federal/components/Blocks/GaleriaImagens/View';
import { IMPRENSA_INITIAL_VALUE } from 'volto-ministerio-publico-federal/components/Blocks/Imprensa/contatos';
import MpfImprensaBlockEdit from 'volto-ministerio-publico-federal/components/Blocks/Imprensa/Edit';
import MpfImprensaBlockView from 'volto-ministerio-publico-federal/components/Blocks/Imprensa/View';
import MpfNewsByThemeBlockEdit from 'volto-ministerio-publico-federal/components/Blocks/NoticiasPorTema/Edit';
import MpfNewsByThemeBlockView from 'volto-ministerio-publico-federal/components/Blocks/NoticiasPorTema/View';
import MpfRadioBlockEdit from 'volto-ministerio-publico-federal/components/Blocks/Radio/Edit';
import MpfRadioBlockView from 'volto-ministerio-publico-federal/components/Blocks/Radio/View';
import MpfSectionHeadingBlockEdit from 'volto-ministerio-publico-federal/components/Blocks/SectionHeading/Edit';
import MpfSectionHeadingBlockView from 'volto-ministerio-publico-federal/components/Blocks/SectionHeading/View';
import MpfVideosBlockEdit from 'volto-ministerio-publico-federal/components/Blocks/Videos/Edit';
import MpfVideosBlockView from 'volto-ministerio-publico-federal/components/Blocks/Videos/View';

/**
 * Blocos de uso geral no corpo do conteúdo.
 *
 * O `group` é OBRIGATÓRIO, ao contrário do que dizia o comentário anterior
 * daqui: o `BlockChooser` do core faz `groupBy(item => item.group)` e depois
 * descarta toda chave que não esteja em `config.blocks.groupBlocksOrder` (ver
 * `components/manage/BlockChooser/BlockChooser.jsx`). Sem `group`, o bloco cai
 * no balde `undefined` e simplesmente não aparece no menu Adicionar — só
 * `mostUsed: true` o salvava, por outro caminho.
 *
 * Grupos do core: `text` (Texto), `media` (Mídia) e `common` (Padrão); os
 * locais estão em `groups.ts`.
 */
export const contentBlocks = {
  mpfNoticiasPorTema: {
    id: 'mpfNoticiasPorTema',
    group: 'common',
    title: 'Notícias por tema',
    icon: newsSVG,
    view: MpfNewsByThemeBlockView,
    edit: MpfNewsByThemeBlockEdit,
    mostUsed: true,
    sidebarTab: 1,
  },
  mpfCampanhas: {
    id: 'mpfCampanhas',
    group: 'common',
    title: 'Campanhas',
    icon: imagesSVG,
    view: MpfCampanhasBlockView,
    edit: MpfCampanhasBlockEdit,
    mostUsed: false,
    sidebarTab: 1,
  },
  // `initialValue` é o gancho do core para dado inicial complexo (ver
  // `helpers/Blocks/Blocks.js`): as 34 unidades entram de uma vez na criação do
  // bloco, sem um `onChangeBlock` logo depois — que corre o risco de perder a
  // gravação. `...value` por último para não atropelar um bloco colado.
  mpfImprensa: {
    id: 'mpfImprensa',
    group: 'common',
    title: 'Imprensa',
    icon: megaphoneSVG,
    view: MpfImprensaBlockView,
    edit: MpfImprensaBlockEdit,
    initialValue: ({ value }) => ({ ...IMPRENSA_INITIAL_VALUE, ...value }),
    mostUsed: false,
    sidebarTab: 1,
  },
  // Galeria de fotos de uma cobertura: palco com legenda e crédito, grade
  // "ver todas" e foto ampliada. Não confundir com o `mpfSubHomeGaleria`, que
  // é a prateleira horizontal de imagens das sub-homes.
  mpfGaleriaImagens: {
    id: 'mpfGaleriaImagens',
    group: 'media',
    title: 'Galeria de imagens',
    icon: imagesSVG,
    view: MpfGaleriaImagensBlockView,
    edit: MpfGaleriaImagensBlockEdit,
    mostUsed: false,
    sidebarTab: 1,
  },

  // A faixa "Rádio e TV" do Figma são estes dois blocos empilhados: o de
  // vídeos com o cabeçalho ligado e o de rádio com ele desligado. Separados
  // para poderem ser usados sozinhos em outras páginas.
  mpfVideos: {
    id: 'mpfVideos',
    group: 'media',
    title: 'Vídeos',
    icon: videoSVG,
    view: MpfVideosBlockView,
    edit: MpfVideosBlockEdit,
    mostUsed: false,
    sidebarTab: 1,
  },
  mpfRadio: {
    id: 'mpfRadio',
    group: 'media',
    title: 'Rádio e podcast',
    icon: audioSVG,
    view: MpfRadioBlockView,
    edit: MpfRadioBlockEdit,
    mostUsed: false,
    sidebarTab: 1,
  },

  mpfSectionHeading: {
    id: 'mpfSectionHeading',
    group: 'text',
    title: 'Título de seção',
    icon: headingSVG,
    view: MpfSectionHeadingBlockView,
    edit: MpfSectionHeadingBlockEdit,
    mostUsed: false,
    sidebarTab: 1,
    blockHasOwnFocusManagement: true,
  },

  mpfAceiteTermos: {
    id: 'mpfAceiteTermos',
    group: 'common',
    title: 'Aceite de termos',
    icon: checkboxSVG,
    view: MpfAceiteTermosBlockView,
    edit: MpfAceiteTermosBlockEdit,
    mostUsed: false,
    sidebarTab: 1,
  },
};

export default contentBlocks;
